import { classNames } from "../../Components"
import { BlockField, BlockRow } from "../../Components/Block"
import { Icon, Icons } from "../../Components/Icon"
import { GetApp } from "../../App"
import { IShipVesselType } from "../../Types"
import { ShipSelectRowViewProps, ShipSelectState, ShipSelectUnavailable } from "./Params"

// One ship in the select list. Renders exactly one control for exactly one state --
// the click target can no longer change meaning based on invisible discovery data, and
// an unavailable row says WHY rather than degrading to an inert label (#1279).
export function ShipSelectRowView({ row, ship, selecting, selectBlocked, onOpen, onConnect }: ShipSelectRowViewProps) {
  const app = GetApp();
  const t = app.getT("ShipSelectView");
  const vesselIcon = ship.vesselType == IShipVesselType.sailing ? Icons.SailingVessel : Icons.MotorVessel;

  // "Found on your network" is only ever shown as a POSITIVE: duplicate advertisements
  // for one account+ship are deliberately omitted upstream, so the absence of the badge
  // says nothing about whether the ship is reachable -- and must not claim it isn't.
  const nearbyBadge = row.nearby ? (
    <span className="ship-select-nearby" title={t("nearby_hint")}>
      <Icon icon={Icons.Wifi} className="icon-nearby" /> {t("nearby")}
    </span>
  ) : null;

  const unavailableReason = (reason: ShipSelectUnavailable): string => {
    switch (reason) {
      case ShipSelectUnavailable.importInProgress:
        return t("unavailable_import_in_progress");
      case ShipSelectUnavailable.noPermission:
        return t("unavailable_no_permission");
      case ShipSelectUnavailable.shipOpenHere:
        // Not rendered per row: the list states this once under its header
        // (`AccountSettings/ShipsView`), since it describes the device, not the ship.
        return "";
    }
  };

  if (row.state == ShipSelectState.unavailable) {
    return (
      <BlockRow>
        <BlockField className="ship-select-unavailable-field">
          <span className="ship-select-unavailable">
            <Icon icon={vesselIcon} /> {ship.name}
          </span>
          {row.unavailable && (
            <span className="ship-select-unavailable-reason">{unavailableReason(row.unavailable)}</span>
          )}
        </BlockField>
      </BlockRow>
    );
  }

  const companion = row.state == ShipSelectState.openElsewhere;
  const label = selecting ? t("selecting") : ship.name;

  return (
    <BlockRow>
      <BlockField>
        <button
          className={classNames([
            "button default",
            "ship-select-button",
            // Kept as the companion class name the E2E helpers key off to detect a
            // ship claimed by another device.
            companion && "ship-select-button-companion",
            row.state == ShipSelectState.openHere && "ship-select-button-primary",
          ])}
          onClick={() => {
            if (companion) {
              // Always the same dialog, whether or not the ship was discovered --
              // `nearby` only decides whether an automatic attempt is worth making.
              onConnect(ship.id, row.nearby);
              return;
            }
            onOpen(ship.id);
          }}
          // Only this row's own availability disables it. Previously ANY open ship
          // disabled EVERY row, which conflated "you have a ship open" with "this ship
          // can't be used" and made the openHere row unclickable (#1279).
          disabled={selectBlocked || row.blocked != undefined}
        >
          {companion && <Icon icon={Icons.Companion} className="icon-companion" />}
          {row.state == ShipSelectState.openHere && <Icon icon={Icons.CircleFilledLarge} className="icon-primary" />}
          <Icon icon={vesselIcon} /> {label}
        </button>
        {nearbyBadge}
      </BlockField>
    </BlockRow>
  );
}
